'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { Search, X, ArrowRight } from 'lucide-react'

type Doc = {
  href: string
  title: string
  excerpt: string | null
  category: string | null
}

// Client-side blog search. Two behaviours:
//   1. On submit (or as you type) it filters the in-memory document
//      set and renders a results dropdown.
//   2. The form's GET action mirrors the WebSite SearchAction JSON-LD
//      target (?q=...), so AI engines that resolve the SearchAction can
//      deep-link straight into pre-populated results.
//
// The searchable haystack is derived client-side from the doc fields.
// It used to be precomputed server-side, which serialised a second
// lowercased copy of every title+excerpt into the SSR HTML and the RSC
// payload — a large share of /blog's 2.6 MB uncompressed page weight
// (flagged by Ahrefs as exceeding Googlebot's render budget).
export function BlogSearch({ docs, initialQuery = '' }: { docs: Doc[]; initialQuery?: string }) {
  const [q, setQ] = useState(initialQuery)
  const [open, setOpen] = useState(initialQuery.length > 0)

  const indexed = useMemo(
    () =>
      docs.map((d) => ({
        d,
        haystack: [d.title, d.excerpt, d.category].filter(Boolean).join(' ').toLowerCase(),
      })),
    [docs]
  )

  // Sync the q param with the URL so the SearchAction deep-link works
  // when the page first renders with ?q=... (e.g. an AI engine prefills).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const initial = params.get('q')
    if (initial && initial !== q) {
      setQ(initial)
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const results = useMemo(() => {
    const query = q.trim().toLowerCase()
    if (!query) return []
    const terms = query.split(/\s+/).filter(Boolean)
    if (terms.length === 0) return []
    const scored = indexed
      .map(({ d, haystack }) => {
        // Score: title hit > category hit > body hit. Multi-term must AND.
        let score = 0
        for (const t of terms) {
          if (!haystack.includes(t)) return { d, score: 0 }
          if (d.title.toLowerCase().includes(t)) score += 5
          else if (d.category?.toLowerCase().includes(t)) score += 2
          else score += 1
        }
        return { d, score }
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
    return scored.map((s) => s.d)
  }, [q, indexed])

  return (
    <form
      role="search"
      action="/blog"
      method="get"
      className="relative"
      onSubmit={(e) => {
        // Don't navigate when we have results, let the user pick.
        if (results.length > 0) e.preventDefault()
      }}
    >
      <label className="relative block">
        <span className="sr-only">Search the blog</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <input
          type="search"
          name="q"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={`Search the blog, try “shoppable video”, “FTC”, “TikTok”…`}
          autoComplete="off"
          className="w-full rounded-full border bg-card py-2 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/15"
        />
        {q && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => { setQ(''); setOpen(false) }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </label>

      {open && q.trim() && (
        <div
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border bg-popover shadow-xl"
          // Keep the input focused on pointer-down so the first tap on a
          // result navigates instead of just dismissing the mobile
          // keyboard (which would otherwise swallow the first tap).
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No matches for <span className="font-medium text-foreground">&ldquo;{q}&rdquo;</span>.
            </p>
          ) : (
            <ul className="max-h-96 divide-y overflow-y-auto">
              {results.map((r) => (
                <li key={r.href}>
                  <Link
                    href={r.href}
                    className="group flex items-start gap-3 px-4 py-3 transition hover:bg-secondary/60"
                    onClick={() => setOpen(false)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-display text-sm font-semibold leading-snug text-foreground group-hover:text-primary transition">
                          {r.title}
                        </p>
                        {r.category && (
                          <span className="ml-2 shrink-0 text-[10px] font-medium uppercase tracking-widest text-primary">
                            {r.category}
                          </span>
                        )}
                      </div>
                      {r.excerpt && (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.excerpt}</p>
                      )}
                    </div>
                    <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary transition group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  )
}
